// Load one audio pack at a time to avoid overlapping large decode allocations.
export class AudioPackCache {
	constructor(createHowl, {now = Date.now, retryDelay = 30000} = {}) {
		this.createHowl = createHowl;
		this.now = now;
		this.retryDelay = retryDelay;
		this.entries = new Map();
		this.retryAfter = new Map();
		this.queue = [];
		this.loading = false;
	}

	get(name) {
		const entry = this.entries.get(name);
		return entry?.loaded && !entry.retired ? entry.howl : null;
	}

	load(name) {
		const existing = this.entries.get(name);
		if (existing) {
			existing.retired = false;
			return existing.promise;
		}
		if (this.now() < (this.retryAfter.get(name) || 0)) return Promise.resolve(null);
		const entry = {name, loaded: false, retired: false};
		entry.promise = new Promise(resolve => { entry.resolve = resolve; });
		this.entries.set(name, entry);
		this.queue.push(entry);
		this.drain();
		return entry.promise;
	}

	drain() {
		if (this.loading) return;
		const entry = this.queue.shift();
		if (!entry) return;
		this.loading = true;
		let finished = false;
		const finish = failed => {
			if (finished) return;
			finished = true;
			entry.loaded = !failed;
			if (failed || entry.retired) {
				entry.howl?.unload();
				this.entries.delete(entry.name);
				if (failed) this.retryAfter.set(entry.name, this.now() + this.retryDelay);
			} else {
				this.retryAfter.delete(entry.name);
			}
			entry.resolve(failed || entry.retired ? null : entry.howl);
			this.loading = false;
			this.drain();
		};
		try {
			entry.howl = this.createHowl(entry.name);
			entry.howl.once('load', () => finish(false));
			entry.howl.once('loaderror', () => finish(true));
			entry.howl.load();
		} catch (error) {
			finish(true);
		}
	}

	stop() {
		for (const entry of this.entries.values()) {
			if (entry.loaded) entry.howl.stop();
		}
	}

	releaseExcept(keep, pendingOnly = false) {
		for (const [name, entry] of this.entries) {
			if (keep.has(name) || (pendingOnly && entry.loaded)) continue;
			entry.retired = true;
			if (entry.loaded || !entry.howl) {
				entry.howl?.unload();
				this.entries.delete(name);
				entry.resolve(null);
			}
			// An in-flight decode cannot be aborted. Let it finish before the
			// next load, then discard its buffer without playing queued sounds.
		}
		this.queue = this.queue.filter(entry => !entry.retired);
	}
}
